/**
 * Presentation-only copy for the narrow Home Chat workflow launcher. The
 * backend still authorizes every entry request; this keeps UI promises aligned
 * with the server-projected workflow list.
 */
export type WorkflowOnlyAbility = 'market_research' | 'gpc' | 'construction';

export type WorkflowOnlyPresentation = {
  abilities: WorkflowOnlyAbility[];
  launcherTitle: string;
  launcherSubtitle: string;
  composerPlaceholder: string;
  panelTitle: string;
  panelSubtitle: string;
  emptyStatus: string;
  emptySavedCopy: string;
};

// Missing availability is a legacy RA compatibility case, not a CE grant.
// A CE construction shell is rendered only from the server's explicit public
// `one_time_construction`/`sow_verification` projection.
const LEGACY_WORKFLOW_ONLY_ABILITIES: WorkflowOnlyAbility[] = ['market_research', 'gpc'];

export const getWorkflowOnlyAbilities = (
  allowedWorkflowTypes: string[] | null | undefined,
): WorkflowOnlyAbility[] => {
  if (allowedWorkflowTypes == null) return LEGACY_WORKFLOW_ONLY_ABILITIES;
  return (['market_research', 'gpc', 'construction'] as WorkflowOnlyAbility[]).filter((ability) => (
    ability === 'construction'
      ? allowedWorkflowTypes.includes('one_time_construction')
        || allowedWorkflowTypes.includes('sow_verification')
      : allowedWorkflowTypes.includes(ability)
  ));
};

/** Returns null when the constrained Home Chat must not expose a shell. */
export const getWorkflowOnlyPresentation = (
  allowedWorkflowTypes: string[] | null | undefined,
  workflowOnlyAudience?: 'ra' | 'ce' | null,
): WorkflowOnlyPresentation | null => {
  // The legacy null fallback predates an audience projection. Once the server
  // supplies the audience, a missing entitlement list cannot imply starters.
  const abilities = workflowOnlyAudience && allowedWorkflowTypes == null
    ? [] : getWorkflowOnlyAbilities(allowedWorkflowTypes);
  // RAs can combine procurement and construction. CE remains a distinct
  // construction-only audience; neither audience grants general FAR chat.
  const chatAbilities = workflowOnlyAudience === 'ce'
    ? abilities.filter(ability => ability === 'construction')
    : abilities;
  const hasMarketResearch = chatAbilities.includes('market_research');
  const hasGpc = chatAbilities.includes('gpc');
  const hasConstruction = chatAbilities.includes('construction');

  if (!hasMarketResearch && !hasGpc && !hasConstruction) {
    return workflowOnlyAudience === 'ra' ? {
      abilities: [],
      launcherTitle: 'Purchase guidance',
      launcherSubtitle: 'Describe what you need. We will show the approved route or where to get help.',
      composerPlaceholder: 'Describe the item or purchase you need help with…',
      panelTitle: 'Purchase guidance',
      panelSubtitle: 'Review the approved purchase route.',
      emptyStatus: 'Checking purchase route…',
      emptySavedCopy: 'No saved purchase guidance chats yet. Describe a purchase above.',
    } : null;
  }
  // A mixed projection needs an explicit RA audience. Legacy/unknown clients
  // cannot infer this access from an ambiguous list of workflow types.
  if (hasConstruction && (hasMarketResearch || hasGpc) && workflowOnlyAudience !== 'ra') return null;
  if (hasConstruction && workflowOnlyAudience === 'ra') {
    const workflows = [
      ...(hasMarketResearch ? ['Commodities Market Research'] : []),
      ...(hasGpc ? ['a GPC purchase'] : []),
      'One-time Construction', 'the Construction Price Estimator',
    ];
    return {
      abilities: chatAbilities,
      launcherTitle: 'Start a workflow',
      launcherSubtitle: `Start ${workflows.join(', ')}. Your workflow will pause when your input is needed.`,
      composerPlaceholder: 'Describe your purchase, construction project, or construction estimate…',
      panelTitle: 'RA workflow',
      panelSubtitle: 'Preparing your authorized workflow.',
      emptyStatus: 'Preparing workflow…',
      emptySavedCopy: 'No saved workflow chats yet. Start a workflow from the options above.',
    };
  }
  // Construction is an intentionally distinct constrained branch. Its
  // controller and native-entry cards are server-issued; this presentation
  // helper only controls CE shell copy and never picks a route from text.
  if (hasConstruction) {
    return {
      abilities: chatAbilities,
      launcherTitle: 'Construction SOW',
      launcherSubtitle: 'Generate a construction SOW or open Construction SOW Verification.',
      composerPlaceholder: 'Describe the construction SOW you need to generate or verify…',
      panelTitle: 'Construction SOW',
      panelSubtitle: 'Preparing your authorized construction workflow.',
      emptyStatus: 'Preparing Construction SOW…',
      emptySavedCopy: 'No saved Construction SOW chats yet. Start a construction workflow from the options above.',
    };
  }
  if (hasMarketResearch && !hasGpc) {
    return {
      abilities: chatAbilities,
      launcherTitle: 'Start Market Research',
      launcherSubtitle: 'Start Commodities Market Research. Your workflow will pause when your input is needed.',
      composerPlaceholder: 'Describe the commodity to research or purchase you need to make…',
      panelTitle: 'Market Research',
      panelSubtitle: 'Preparing your authorized Market Research workflow.',
      emptyStatus: 'Preparing Market Research…',
      emptySavedCopy: 'No saved Market Research chats yet. Start a workflow from the options above.',
    };
  }
  if (hasGpc && !hasMarketResearch) {
    return {
      abilities: chatAbilities,
      launcherTitle: 'Start a GPC purchase',
      launcherSubtitle: 'Start a GPC purchase. Your workflow will pause when your input is needed.',
      composerPlaceholder: 'Describe the commodity to research or purchase you need to make…',
      panelTitle: 'GPC purchase',
      panelSubtitle: 'Preparing your authorized GPC purchase.',
      emptyStatus: 'Preparing GPC purchase…',
      emptySavedCopy: 'No saved GPC purchase chats yet. Start a workflow from the options above.',
    };
  }
  if (hasMarketResearch && hasGpc && !hasConstruction) {
    return {
      abilities: chatAbilities,
      launcherTitle: 'Start a procurement workflow',
      launcherSubtitle: 'Start Commodities Market Research or a GPC purchase. Your workflow will pause when your input is needed.',
      composerPlaceholder: 'Describe the commodity to research or purchase you need to make…',
      panelTitle: 'Procurement workflow',
      panelSubtitle: 'Preparing your authorized workflow.',
      emptyStatus: 'Preparing workflow…',
      emptySavedCopy: 'No saved Market Research or GPC chats yet. Start a workflow from the options above.',
    };
  }
  return null;
};
